import React, { useEffect, useState } from "react";
import { api, getErr } from "@/lib/api";
import { toast } from "sonner";
import {
  UserPlus,
  Copy,
  ShareNetwork,
  Medal,
  CurrencyDollar,
  UsersThree,
  PauseCircle,
} from "@phosphor-icons/react";

function fmtMoney(n) {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

const STATE_LABELS = {
  signup: { label: "In progress", cls: "bg-[#9CA3AF] text-white" },
  profile_complete: { label: "Awaiting review", cls: "bg-amber-500 text-white" },
  qualified: { label: "Bounty earned", cls: "bg-[#0044FF] text-white" },
  tier_upgraded: { label: "Tier upgraded", cls: "bg-violet-600 text-white" },
  first_job_bonus_earned: { label: "First job complete", cls: "bg-emerald-700 text-white" },
  closed: { label: "Closed", cls: "bg-[#4B5563] text-white" },
  already_in_network: { label: "Already in network", cls: "bg-[#4B5563] text-white" },
  blocked: { label: "Blocked", cls: "bg-red-600 text-white" },
  clawed_back: { label: "Reversed", cls: "bg-red-700 text-white" },
};

function StateBadge({ state }) {
  const s = STATE_LABELS[state] || STATE_LABELS.signup;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${s.cls}`}>
      {s.label}
    </span>
  );
}

const TIERS = [
  { tier: 1, who: "General Labor worker reaches ACTIVE", bounty: "$1.00" },
  { tier: 2, who: "Verified specialist (handyman, painting, landscaping, pressure washing…)", bounty: "$3.00" },
  { tier: 3, who: "License-verified pro (plumbing, electrical, HVAC)", bounty: "$5.00" },
];

export default function VARecruiting() {
  const [resp, setResp] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/va/recruiting/overview");
      setResp(data);
    } catch (e) {
      setErr(getErr(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const link = resp
    ? `${window.location.origin}/register?recruit=${resp.recruit_code}`
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Recruit link copied");
    } catch {
      toast.error("Could not copy — long-press the link to copy manually");
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join the HCOB Network",
          text: "Join a network of specialists — I'll help you get set up.",
          url: link,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="va-recruiting">
      <div className="mb-6">
        <div className="font-mono-label">Recruiting</div>
        <h1 className="font-display text-4xl font-black tracking-tight">Recruit workers, earn bounties</h1>
        <p className="mt-2 max-w-2xl text-sm text-[#4B5563]">
          Share your link. When your recruit finishes onboarding (profile, agreement, W-9) you earn a
          tiered bounty — plus 5% of their first paid job. One-time per recruit, paid on your monthly
          commission statement. Their pay is never reduced.
        </p>
      </div>

      {err && (
        <div className="mb-6 border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="recruiting-error">
          {err}
        </div>
      )}

      {resp?.paused && (
        <div className="mb-6 flex items-center gap-2 border border-amber-400 bg-amber-50 p-3 text-sm text-amber-800" data-testid="recruiting-paused-banner">
          <PauseCircle size={18} weight="duotone" />
          Your recruiting link is paused pending admin review. New signups won't be attributed until it's re-enabled.
        </div>
      )}

      {/* Link + code */}
      <div className="mb-6 border border-[#030712] bg-white p-5" data-testid="recruit-link-card">
        <div className="font-mono-label mb-2">Your recruit link</div>
        <div className="flex flex-wrap items-center gap-3">
          <code className="max-w-full overflow-x-auto whitespace-nowrap border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 text-xs" data-testid="recruit-link">
            {resp ? link : "Loading…"}
          </code>
          <button
            data-testid="copy-recruit-link-btn"
            onClick={copyLink}
            disabled={!resp}
            className="inline-flex h-9 items-center gap-1.5 bg-[#030712] px-3 text-xs font-semibold text-white hover:bg-[#1F2937]"
          >
            <Copy size={14} /> Copy
          </button>
          <button
            data-testid="share-recruit-link-btn"
            onClick={shareLink}
            disabled={!resp}
            className="inline-flex h-9 items-center gap-1.5 border border-[#030712] px-3 text-xs font-semibold hover:bg-[#F9FAFB]"
          >
            <ShareNetwork size={14} /> Share
          </button>
        </div>
        <div className="mt-3 text-xs text-[#4B5563]">
          Code: <span className="font-mono font-bold" data-testid="recruit-code">{resp?.recruit_code || "…"}</span>
          {" "}— recruits can also type it in the “Recruiter code” field at signup.
        </div>
      </div>

      {/* Totals */}
      <div className="mb-6 grid grid-cols-3 gap-4">
        <div className="border border-[#E5E7EB] bg-white p-5" data-testid="recruiting-totals-signups">
          <div className="flex items-center gap-2 font-mono-label"><UsersThree size={14} /> Recruits</div>
          <div className="mt-2 font-display text-3xl font-black">{resp?.totals?.signups ?? "—"}</div>
        </div>
        <div className="border border-[#E5E7EB] bg-white p-5" data-testid="recruiting-totals-activated">
          <div className="flex items-center gap-2 font-mono-label"><Medal size={14} /> Activated</div>
          <div className="mt-2 font-display text-3xl font-black">{resp?.totals?.activated ?? "—"}</div>
        </div>
        <div className="border border-emerald-400 bg-white p-5" data-testid="recruiting-totals-earned">
          <div className="flex items-center gap-2 font-mono-label"><CurrencyDollar size={14} /> Earned</div>
          <div className="mt-2 font-display text-3xl font-black text-emerald-700">{fmtMoney(resp?.totals?.earned)}</div>
        </div>
      </div>

      {/* Program explainer */}
      <div className="mb-6 border border-[#E5E7EB] bg-white" data-testid="recruiting-tier-table">
        <div className="border-b border-[#E5E7EB] px-5 py-3 font-mono-label">How you earn</div>
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB]">
            <tr className="text-left font-mono uppercase text-[10px] tracking-widest text-[#4B5563]">
              <th className="px-5 py-2">Tier</th>
              <th className="px-5 py-2">Your recruit</th>
              <th className="px-5 py-2 text-right">Bounty</th>
            </tr>
          </thead>
          <tbody>
            {TIERS.map((t) => (
              <tr key={t.tier} className="border-t border-[#E5E7EB]">
                <td className="px-5 py-2 font-display font-black">Tier {t.tier}</td>
                <td className="px-5 py-2 text-[#4B5563]">{t.who}</td>
                <td className="px-5 py-2 text-right font-mono font-bold">{t.bounty}</td>
              </tr>
            ))}
            <tr className="border-t border-[#E5E7EB] bg-emerald-50">
              <td className="px-5 py-2 font-display font-black text-emerald-800">Bonus</td>
              <td className="px-5 py-2 text-emerald-800">
                5% of your recruit's <strong>first completed & paid job</strong> (within 180 days of activation)
              </td>
              <td className="px-5 py-2 text-right font-mono font-bold text-emerald-800">5%</td>
            </tr>
          </tbody>
        </table>
        <div className="border-t border-[#E5E7EB] px-5 py-3 text-xs text-[#4B5563]">
          If your recruit verifies a higher tier within <strong>90 days</strong> of activating, you're automatically
          paid the difference. This is a <strong>one-time bounty per recruit</strong> — not a recurring tail like
          client-account commissions.
        </div>
      </div>

      {/* Pipeline */}
      {!resp ? (
        <div className="font-mono-label">Loading…</div>
      ) : resp.items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 border border-dashed border-[#E5E7EB] bg-white p-12 text-center" data-testid="recruiting-empty">
          <UserPlus size={36} weight="duotone" className="text-[#4B5563]" />
          <div className="font-display text-lg font-black">No recruits yet</div>
          <div className="max-w-md text-sm text-[#4B5563]">
            Share your link with workers you know. Suggested pitch: “Join a network of specialists — I'll help you get set up.”
          </div>
        </div>
      ) : (
        <div className="border border-[#E5E7EB] bg-white" data-testid="recruiting-pipeline">
          <div className="border-b border-[#E5E7EB] px-5 py-3 font-mono-label">Your recruit pipeline</div>
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB]">
              <tr className="text-left font-mono uppercase text-[10px] tracking-widest text-[#4B5563]">
                <th className="px-4 py-3">Recruit</th>
                <th className="px-4 py-3">Signed up</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Tier</th>
                <th className="px-4 py-3 text-right">Earned</th>
              </tr>
            </thead>
            <tbody>
              {resp.items.map((r) => (
                <tr key={r.recruit_id} data-testid={`recruit-row-${r.recruit_id}`} className="border-t border-[#E5E7EB] hover:bg-[#F9FAFB]">
                  <td className="px-4 py-3 font-semibold">{r.name}</td>
                  <td className="px-4 py-3 text-xs text-[#4B5563]">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3"><StateBadge state={r.state} /></td>
                  <td className="px-4 py-3 font-mono">{r.tier ? `Tier ${r.tier}` : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">
                    {r.earned > 0 ? fmtMoney(r.earned) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
